import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { TutorialProvider } from './context/TutorialContext';
import { OfflineProvider } from './offline/OfflineContext';
import { ThemeProvider } from './context/ThemeContext';
import PrivateRoute from './components/PrivateRoute';
import ByopdfLoader from './components/ByopdfLoader';
import lazyWithReload from './utils/lazyWithReload';
import FirebaseAuth from './components/FirebaseAuth';
import Dashboard from './pages/Dashboard';
import TutorialPopup from './components/TutorialPopup';
import AuthNotification from './components/AuthNotification';
import { useTutorial } from './context/TutorialContext';

const Purchase = lazyWithReload(() => import('./pages/Purchase'));
const Checkout = lazyWithReload(() => import('./pages/Checkout'));
const FAQ = lazyWithReload(() => import('./pages/FAQ'));
const SharedStory = lazyWithReload(() => import('./pages/SharedStory'));
const Terms = lazyWithReload(() => import('./pages/Terms'));
const Privacy = lazyWithReload(() => import('./pages/Privacy'));
const Subscription = lazyWithReload(() => import('./pages/Settings'));
const Insights = lazyWithReload(() => import('./pages/Insights'));
const Rewards = lazyWithReload(() => import('./pages/Rewards'));
const PaymentSuccess = lazyWithReload(() => import('./pages/PaymentSuccess')); // Import Success page
const PaymentFailure = lazyWithReload(() => import('./pages/PaymentFailure')); // Import Failure page
const AdminDashboard = lazyWithReload(() => import('./pages/AdminDashboard')); // Dev-only local ops console
const PlayerTest = lazyWithReload(() => import('./pages/PlayerTest')); // Dev-only player layout harness
const StoryTimeTest = lazyWithReload(() => import('./pages/StoryTimeTest')); // Dev-only Story Time wizard harness
const HabitHeroTest = lazyWithReload(() => import('./pages/HabitHeroTest')); // Dev-only habit hero harness
const UploaderTest = lazyWithReload(() => import('./pages/UploaderTest')); // Dev-only uploader wizard harness
const LibraryCardTest = lazyWithReload(() => import('./pages/LibraryCardTest')); // Dev-only library card harness
const CelebrationTest = lazyWithReload(() => import('./pages/CelebrationTest')); // Dev-only book-finished celebration harness
const PaymentStatusTest = lazyWithReload(() => import('./pages/PaymentStatusTest')); // Dev-only payment result harness
const BadgesTest = lazyWithReload(() => import('./pages/BadgesTest')); // Dev-only badge shelf harness
const RewardsTest = lazyWithReload(() => import('./pages/RewardsTest')); // Dev-only rewards page harness
const PlatesTest = lazyWithReload(() => import('./pages/PlatesTest')); // Dev-only bookplate art harness
const LoaderTest = lazyWithReload(() => import('./pages/LoaderTest')); // Dev-only loading mark harness
const KnowledgeTest = lazyWithReload(() => import('./pages/KnowledgeTest')); // Dev-only knowledge card harness
const CreditDisplayTest = lazyWithReload(() => import('./pages/CreditDisplayTest')); // Dev-only credit display harness
const BlendTest = lazyWithReload(() => import('./pages/BlendTest')); // Signature-voice audition gallery (live so auditions work on prod)
const VoiceCardsTest = lazyWithReload(() => import('./pages/VoiceCardsTest'));
const OfflineLab = lazyWithReload(() => import('./pages/OfflineLab'));
const Downloads = lazyWithReload(() => import('./pages/Downloads')); // On-device library; no auth, works offline // On-device canary for installed-app storage and locked-screen playback (live: it runs on phones) // Narrator character cards lineup (live so auditions work on prod)

const Footer = () => (
  <footer style={{
    padding: '20px',
    textAlign: 'center',
    borderTop: '1px solid var(--border-color)',
    marginTop: 'auto'
  }}>
    <nav style={{
      display: 'flex',
      justifyContent: 'center',
      gap: '20px',
      fontSize: '0.9rem'
    }}>
      <a href="/terms" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Terms of Service</a>
      <a href="/privacy" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>Privacy Policy</a>
      <a href="/faq" style={{ color: 'var(--text-secondary)', textDecoration: 'none' }}>FAQ</a>
    </nav>
  </footer>
);

// Shown while a lazily loaded page's code arrives (first visit to that page).
const RouteFallback = () => (
  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 20px' }}>
    <ByopdfLoader size={56} />
  </div>
);

const AppContent = () => {
  const { showTutorial, hideProcessingTutorial } = useTutorial();
  const location = useLocation();

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <AuthNotification />
      <div className="route-view" key={location.pathname} style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Suspense fallback={<RouteFallback />}>
      <Routes location={location}>
        <Route path="/auth" element={<FirebaseAuth />} />
        <Route path="/purchase" element={<Purchase />} />
        {/* Landing-page purchase flow: /checkout?plan=annual|monthly */}
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/settings" element={<PrivateRoute><Subscription /></PrivateRoute>} />
        <Route path="/insights" element={<PrivateRoute><Insights /></PrivateRoute>} />
        <Route path="/rewards" element={<PrivateRoute><Rewards /></PrivateRoute>} />
        <Route path="/subscription" element={<PrivateRoute><Subscription /></PrivateRoute>} /> {/* Add Subscription route */}
        <Route path="/faq" element={<FAQ />} />
        <Route path="/s/:token" element={<SharedStory />} /> {/* public Story Time share page */}
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/payment-success" element={<PrivateRoute><PaymentSuccess /></PrivateRoute>} /> {/* Add Success route */}
        <Route path="/payment-failure" element={<PrivateRoute><PaymentFailure /></PrivateRoute>} /> {/* Add Failure route */}
        <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        {process.env.NODE_ENV === 'development' && (
          <Route path="/admin" element={<AdminDashboard />} />
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/story-time-test" element={<StoryTimeTest />} />
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/player-test" element={<PlayerTest />} />
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/habit-hero-test" element={<HabitHeroTest />} />
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/uploader-test" element={<UploaderTest />} />
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/library-card-test" element={<LibraryCardTest />} />
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/celebration-test" element={<CelebrationTest />} />
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/payment-status-test" element={<PaymentStatusTest />} />
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/badges-test" element={<BadgesTest />} />
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/credit-display-test" element={<CreditDisplayTest />} />
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/rewards-test" element={<RewardsTest />} />
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/plates-test" element={<PlatesTest />} />
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/loader-test" element={<LoaderTest />} />
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/knowledge-test" element={<KnowledgeTest />} />
        )}
        {process.env.NODE_ENV === 'development' && (
          <Route path="/settings-test" element={<Subscription />} /> // Dev-only settings layout harness (renders logged-out shell)
        )}
        <Route path="/blend-test" element={<BlendTest />} />
        <Route path="/voice-cards-test" element={<VoiceCardsTest />} />
        <Route path="/lab/offline" element={<OfflineLab />} />
        <Route path="/downloads" element={<Downloads />} />
        {/* Optional: Add a catch-all route or redirect for unknown paths */}
      </Routes>
      </Suspense>
      </div>
      {showTutorial && <TutorialPopup onClose={hideProcessingTutorial} />}
      {/* The marketing footer is hidden inside the app shell (these screens have
          their own nav) so the app fills the viewport snugly. */}
      {!['/', '/insights', '/rewards', '/settings', '/subscription'].includes(location.pathname) && <Footer />}
    </div>
  );
};

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <OfflineProvider>
            <TutorialProvider>
              <AppContent />
            </TutorialProvider>
          </OfflineProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
